import "./assets/styles/theme.css";
import { useEffect, useRef, useState } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";
import LoginPage from "./pages/Login/LoginPage";
import InternDashboard from "./pages/Intern/InternDashboard";
import EmployeeDashboard from "./pages/Employee/EmployeeDashboard";
import HrDashboard from "./pages/HR/HrDashboard";
import ITAdminDashboard from "./pages/ITAdmin/ITAdminDashboard";
import PMDashboard from "./pages/PM/PMDashboard";
import SuperAdminPanel from "./pages/SuperAdmin/SuperAdminPanel";
import OnboardingFlow from "./pages/Onboarding/OnboardingFlow";
import PendingVerificationScreen from "./pages/Onboarding/PendingVerification";

function AppActionPopupHost() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      const text = String(e?.detail?.message || "").trim();
      if (!text) return;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setItems((prev) => [...prev, { id, text }]);
      setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== id));
      }, 3200);
    };

    window.addEventListener("app:notify", handler);
    return () => window.removeEventListener("app:notify", handler);
  }, []);

  if (!items.length) return null;

  return (
    <div style={{ position: "fixed", right: 18, bottom: 18, zIndex: 9999, display: "grid", gap: 8, maxWidth: 360 }}>
      {items.map((i) => (
        <div key={i.id} style={{ background: "#0f172a", color: "#fff", borderRadius: 10, padding: "10px 12px", boxShadow: "0 10px 26px rgba(15,23,42,.25)", fontSize: 12, fontWeight: 600 }}>
          {i.text}
        </div>
      ))}
    </div>
  );
}

function AppRoutes() {
  const { user, logout, setUser, refreshUser, loading } = useAuth();
  const authCheckedRef = useRef(false);

  useEffect(() => {
    const nativeAlert = window.alert?.bind(window);
    window.alert = (message) => {
      const text = String(message || "").trim();
      if (!text) return;
      window.dispatchEvent(new CustomEvent("app:notify", { detail: { message: text } }));
    };
    return () => {
      if (nativeAlert) window.alert = nativeAlert;
    };
  }, []);

  useEffect(() => {
    if (!loading && !user && !authCheckedRef.current) {
      authCheckedRef.current = true;
    }
  }, [loading, user]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontFamily: "DM Sans,sans-serif",
          color: "#6366f1",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        <div className="ob-pending-dot" style={{ margin: "0 auto" }} />
        <div>Loading your dashboard...</div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const st = user.status || "";

  if (st === "TEMP") {
    return (
      <OnboardingFlow
        user={user}
        onSubmit={async () => {
          setUser((prev) => ({ ...prev, status: "ONBOARDING_SUBMITTED" }));
          try {
            if (refreshUser) await refreshUser();
          } catch (_) {}
        }}
        onLogout={logout}
      />
    );
  }

  if (st === "ONBOARDING_SUBMITTED") {
    return <PendingVerificationScreen user={user} onLogout={logout} />;
  }

  switch (user.role) {
    case "employee":
      return <EmployeeDashboard onLogout={logout} user={user} />;
    case "hr":
      return <HrDashboard onLogout={logout} />;
    case "intern":
      return <InternDashboard onLogout={logout} user={user} />;
    case "itadmin":
      return <ITAdminDashboard onLogout={logout} />;
    case "pm":
      return <PMDashboard onLogout={logout} />;
    case "superadmin":
      return <SuperAdminPanel onLogout={logout} />;
    default:
      return <LoginPage />;
  }
}

export default function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <AppRoutes />
        <AppActionPopupHost />
      </SocketProvider>
    </AuthProvider>
  );
}
